<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-view-grid-outline" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [formControl]="filterControl" placeholder="Search" />
    @if (filterControl.value) {
    <button mat-icon-button matSuffix (click)="filterControl.setValue(''); $event.stopPropagation()" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

<mat-menu #addMenu="matMenu" [overlapTrigger]="false">
  <button mat-menu-item (click)="addOrEditCard(null, false, false)" title="Add a new card">New Card</button>
  @for (template of templateList; track template.id) {
  <button mat-menu-item (click)="addOrEditCard(template, false, true)" title="{{ template.description }}">{{
    template.name }}</button>
  }
</mat-menu>

<div class="table-container">
  <table mat-table #cardTable [dataSource]="cardDataSource" matSort (matSortChange)="sortChanged($event)" multiTemplateDataRows>
    <!-- action column -->
    <ng-container matColumnDef="action">
      <th mat-header-cell *matHeaderCellDef class="column-action">
        @if (showTemplates && canEdit) {
        <button mat-icon-button (click)="addOrEditCard(null, false, false); $event.stopPropagation();"
          title="Add a template card" style="outline: none;">
          <mat-icon class="mdi-24px center-self" fontIcon="mdi-plus-circle"></mat-icon>
        </button>
        <button mat-icon-button title="Upload Card templates from JSON" style="outline: none;"
          (click)="jsonInput.click(); $event.stopPropagation();">
          <mat-icon class="mdi-24px" fontIcon="mdi-upload"></mat-icon>
        </button>
        <button mat-icon-button title="Download Card templates as JSON" style="outline: none;"
          (click)="openDownloadDialog(); $event.stopPropagation();">
          <mat-icon class="mdi-24px" fontIcon="mdi-download"></mat-icon>
        </button>
        }
        @if (!showTemplates && (canEdit || msel.hasRole(loggedInUserId, null).owner)) {
        <button mat-icon-button [matMenuTriggerFor]="addMenu"
          style="outline: none;" title="Add card"
          (click)="$event.stopPropagation();">
          <mat-icon class="mdi-24px self-center" fontIcon="mdi-plus-circle"></mat-icon>
        </button>
        }
      </th>
      <td mat-cell *matCellDef="let element" class="column-action">
        @if (canEdit || msel.hasRole(loggedInUserId, null).owner) {
        <button mat-icon-button (click)="addOrEditCard(element, false, false); $event.stopPropagation()"
          class="all-clicks" title="Edit {{ element.name }}">
          <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"></mat-icon>
        </button>
        }
        @if (canEdit || msel.hasRole(loggedInUserId, null).owner) {
        <button mat-icon-button (click)="deleteCard(element); $event.stopPropagation()" class="all-clicks"
          title="Delete {{ element.name }}">
          <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"></mat-icon>
        </button>
        }
        @if (!showTemplates && canEdit) {
        <button mat-icon-button (click)="addOrEditCard(element, true, false); $event.stopPropagation();"
          title="Make {{ element.name }} a template">
          <mat-icon class="mdi-18px" fontIcon="mdi-card-plus-outline"
            title="Make {{ element.name }} a template"></mat-icon>
        </button>
        }
      </td>
    </ng-container>

    <!-- move column -->
    <ng-container matColumnDef="move">
      <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-move">Move</th>
      <td mat-cell *matCellDef="let element" class="column-move">{{ element.move }}</td>
    </ng-container>

    <!-- name column -->
    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-name">Name</th>
      <td mat-cell *matCellDef="let element" class="column-name">{{ element.name }}</td>
    </ng-container>

    <!-- description column -->
    <ng-container matColumnDef="description">
      <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-description">
        Description
      </th>
      <td mat-cell *matCellDef="let element" class="column-description">{{ element.description }}</td>
    </ng-container>

    <!-- Expanded Content Column -->
    <ng-container matColumnDef="expandedDetail">
      <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
        <div [@detailExpand]="element.id == expandedElementId ? 'expanded' : 'collapsed'" style="overflow: hidden">
          @if (expandedElementId === element.id) {
          <div class="expanded-detail-div" (click)="$event.stopPropagation();">
            <div class="single-detail">
              <span class="select-title">Select the MSEL teams that will see this card</span>
            </div>
            <div class="single-detail">
              <app-card-teams style="width: 100%;" [cardId]="element.id" [loggedInUserId]="loggedInUserId"></app-card-teams>
            </div>
          </div>
          }
        </div>
      </td>
    </ng-container>

    <!-- row definitions -->
    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
    <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class]="getRowClass(row.id)" (click)="rowClicked(row)"></tr>
    <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
  </table>
</div>

<input hidden type="file" #jsonInput accept=".json" (change)="selectFile($event)" />
